import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/menuData";
import { BookingDialog } from "./BookingDialog";

export const Contact = () => {
  const { t } = useLang();
  const waLink = `https://wa.me/${BRAND.phone.replace("+", "")}?text=${encodeURIComponent(
    "Halo Hayalan Coffee, saya mau tanya."
  )}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-brand-cream"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-brand-blue font-medium">
              {t.contact.eyebrow}
            </span>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight text-brand-ink">
              {t.contact.title}
            </h2>

            <ul className="mt-10 space-y-7">
              <li className="flex gap-4" data-testid="contact-address">
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                  <MapPin className="w-5 h-5" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-mocha font-medium">
                    {t.contact.address_label}
                  </p>
                  <p className="mt-1.5 text-base text-brand-ink leading-relaxed">
                    {t.contact.address}
                  </p>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-phone">
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                  <Phone className="w-5 h-5" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-mocha font-medium">
                    {t.contact.phone_label}
                  </p>
                  <a
                    href={`tel:${BRAND.phone}`}
                    className="mt-1.5 block text-base text-brand-ink font-medium hover:text-brand-blue"
                  >
                    {t.contact.phone}
                  </a>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-hours">
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                  <Clock className="w-5 h-5" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-mocha font-medium">
                    {t.contact.hours_label}
                  </p>
                  <p className="mt-1.5 text-base text-brand-ink leading-relaxed">
                    {t.contact.hours}
                  </p>
                </div>
              </li>
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={BRAND.mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-maps-cta"
                className="group inline-flex items-center gap-2 bg-brand-blue text-white px-6 py-3.5 rounded-full text-sm font-medium hover:bg-blue-800 transition-all hover:-translate-y-0.5"
              >
                {t.contact.cta_directions}
                <ArrowUpRight
                  className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={2}
                />
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-cta"
                className="inline-flex items-center gap-2 border border-brand-coffee text-brand-coffee px-6 py-3.5 rounded-full text-sm font-medium hover:bg-brand-coffee hover:text-brand-cream transition-all"
              >
                {t.contact.cta_whatsapp}
              </a>
              <BookingDialog />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7"
            data-testid="contact-map"
          >
            <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-square rounded-[2rem] overflow-hidden ring-1 ring-brand-border bg-brand-sand">
              <iframe
                title="Hayalan Coffee Location"
                src={BRAND.mapsEmbed}
                width="100%"
                height="100%"
                className="border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
